import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import { getCanonicalUrl } from '../../../utils/seo';
const halloweenVideo = 'https://BelvoirCastle.b-cdn.net/videos/halloween.mp4';

const PEACOCK   = 'https://www.belvoircastle.com/wp/wp-content/uploads/2026/04/white-peacock.png';
const HERO_IMG  = 'https://BelvoirCastlePull.b-cdn.net/Website/5S5A1084-2%20(1).webp';
const IMG_GIRL_CURSE = 'https://BelvoirCastlePull.b-cdn.net/Website/girl-curse.webp';
const IMG_HALLOWEEN_BOY = 'https://BelvoirCastlePull.b-cdn.net/Website/halloween-boy%5D.webp';
const IMG_WITCHES = 'https://BelvoirCastlePull.b-cdn.net/Website/5S5A1084-2%20(1).webp';
const IMG_WITCH_ZIP = 'https://BelvoirCastlePull.b-cdn.net/Website/5S5A1400%20(1).webp';
const IMG_WITCHES_PATH = 'https://BelvoirCastlePull.b-cdn.net/Website/Screenshot%202026-08-12%20at%2012.31.20.png';
const IMG_WITCH_PURPLE = 'https://BelvoirCastlePull.b-cdn.net/Website/Screenshot%202026-08-12%20at%2012.29.10.webp';
const IMG_WITCH_FEATHERS = 'https://BelvoirCastlePull.b-cdn.net/Website/Screenshot%202026-08-12%20at%2012.28.46.webp';
const IMG_BOY_GOAT = 'https://BelvoirCastlePull.b-cdn.net/Website/boy-goat.webp';
const IMG_CRAFTS         = 'https://BelvoirCastlePull.b-cdn.net/Website/Screenshot%202026-08-12%20at%2012.25.55.webp';
const IMG_CRAFTS_2       = 'https://BelvoirCastlePull.b-cdn.net/Website/Screenshot%202026-08-12%20at%2012.29.45.webp';
const IMG_CRAFTS_HERO    = 'https://BelvoirCastle.b-cdn.net/Halloween/crafts.webp';
const IMG_PLAY_CLIMB     = 'https://BelvoirCastlePull.b-cdn.net/Website/5S5A1482%20(1).jpg';
const IMG_PLAY_ZIP       = 'https://BelvoirCastlePull.b-cdn.net/Website/play1%20(1).webp';
const IMG_DONKEYS        = 'https://BelvoirCastlePull.b-cdn.net/Website/IMG_1835%20(1).webp';
const IMG_PLAY_TOWER     = 'https://BelvoirCastlePull.b-cdn.net/Website/7.png';
const IMG_COLLAGE        = 'https://BelvoirCastlePull.b-cdn.net/Website/collage%20higher%20res.png';

const H = {
  ink:      '#14101F',
  midnight: '#1C1630',
  torch:    '#E07A2F',
  candle:   '#C9A227',
  moss:     '#3D5C4A',
  plum:     '#5C3A4E',
  bone:     '#F6F0E6',
  parchment:'#EDE6D4',
  stone:    '#9A958C',
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── PAGE SHELL ─── */
const Page = styled.div`
  min-height: 100vh;
  background: ${H.bone};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

/* ─── HERO ─── */
const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(620px, 82vh, 900px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;

  @media (max-width: 768px) {
    height: auto;
    min-height: clamp(600px, 88svh, 780px);
  }
`;

const HeroCover = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 78%;
`;

const HeroVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 40%;
  opacity: ${p => (p.$visible ? 1 : 0)};
  transition: opacity 1.2s ease-in-out;
`;

const HeroImageThenVideo = ({ image, videoSrc, delay = 3000 }) => {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [allowVideo, setAllowVideo] = useState(false);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setAllowVideo(!reduceMotion.matches);
    update();
    reduceMotion.addEventListener('change', update);
    return () => reduceMotion.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!allowVideo) {
      setStarted(false);
      setPlaying(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setStarted(true), delay);
    return () => window.clearTimeout(timer);
  }, [allowVideo, delay]);

  useEffect(() => {
    if (!started || !allowVideo) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', 'true');

    const tryPlay = () => {
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    };

    tryPlay();
    video.addEventListener('canplay', tryPlay);

    const onGesture = () => {
      tryPlay();
      window.removeEventListener('touchstart', onGesture);
      window.removeEventListener('click', onGesture);
    };
    window.addEventListener('touchstart', onGesture, { passive: true });
    window.addEventListener('click', onGesture);

    return () => {
      video.removeEventListener('canplay', tryPlay);
      window.removeEventListener('touchstart', onGesture);
      window.removeEventListener('click', onGesture);
    };
  }, [started, allowVideo, videoSrc]);

  return (
    <>
      <HeroCover src={image} alt="The pesky witches on the adventure playground — Belvoir's Enchanted Halloween Trail" />
      {started && allowVideo && (
        <HeroVideo
          ref={videoRef}
          src={videoSrc}
          $visible={playing}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onPlaying={() => setPlaying(true)}
        />
      )}
    </>
  );
};

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to top,
      rgba(20,16,31,0.98) 0%,
      rgba(20,16,31,0.92) 18%,
      rgba(20,16,31,0.68) 34%,
      rgba(20,16,31,0.22) 48%,
      transparent 62%
    );

  @media (max-width: 768px) {
    background:
      linear-gradient(to top,
        rgba(20,16,31,0.99) 0%,
        rgba(20,16,31,0.94) 24%,
        rgba(20,16,31,0.72) 40%,
        rgba(20,16,31,0.28) 54%,
        transparent 68%
      );
  }
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.1;
  background-image:
    repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(224,122,47,0.4) 38px, rgba(224,122,47,0.4) 40px),
    repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(92,58,78,0.45) 38px, rgba(92,58,78,0.45) 40px);
  pointer-events: none;
  mask-image: linear-gradient(to top, black 0%, transparent 42%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(1.75rem, 4.5vw, 3rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
  animation: ${fadeUp} 0.7s ease both;

  @media (max-width: 768px) {
    padding: 1.25rem 1.1rem 1.6rem;
  }
`;

const HeroKicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${H.torch};
  text-shadow: 0 1px 12px rgba(20,16,31,0.55);

  @media (max-width: 480px) {
    font-size: 0.62rem;
  }
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.55rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2.4rem, 5.8vw, 4.4rem);
  font-weight: 700;
  line-height: 1.0;
  color: #fff;
  text-shadow: 0 2px 28px rgba(20,16,31,0.65);

  em {
    font-style: italic;
    color: ${H.candle};
  }

  @media (max-width: 768px) {
    font-size: clamp(2.05rem, 9.5vw, 2.6rem);
    margin-bottom: 0.45rem;
  }
`;

const HeroStrapline = styled.p`
  margin: 0 0 1.15rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.05rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 400;
  color: rgba(255,255,255,0.92);
  max-width: 36rem;
  text-shadow: 0 2px 18px rgba(20,16,31,0.55);

  @media (max-width: 768px) {
    margin-bottom: 0.9rem;
    font-size: 1rem;
    line-height: 1.45;
  }
`;

const HeroBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem 0.7rem;
  margin-bottom: 1.15rem;

  @media (max-width: 768px) {
    gap: 0.4rem;
    margin-bottom: 0.85rem;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => p.$fill ? H.torch : 'rgba(20,16,31,0.55)'};
  color: #fff;
  border: 1px solid ${p => p.$fill ? 'transparent' : 'rgba(255,255,255,0.35)'};
  backdrop-filter: blur(10px);

  @media (max-width: 768px) {
    font-size: 0.62rem;
    padding: 0.4rem 0.75rem;
    line-height: 1.35;
  }
`;

const HeroButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.55rem;
    width: 100%;

    button, a {
      width: 100%;
      padding: 0.85rem 1.25rem;
      font-size: 0.75rem;
      text-align: center;
    }
  }
`;

const BtnPrimary = styled.button`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${H.torch};
  color: #fff;
  box-shadow: 0 6px 24px rgba(224,122,47,0.44);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(224,122,47,0.54);
  }
`;

const BtnGhost = styled.button`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.65);
  cursor: pointer;
  background: rgba(255,255,255,0.07);
  color: #fff;
  backdrop-filter: blur(8px);
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255,255,255,0.16);
  }
`;

/* ─── GROUP SAVER BANNER ─── */
const OfferBanner = styled.div`
  background: linear-gradient(135deg, ${H.plum} 0%, #3a2432 100%);
  padding: clamp(1rem, 2.5vw, 1.4rem) clamp(1rem, 4vw, 2rem);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  text-align: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 1.1rem 1rem 1.25rem;

    button {
      width: 100%;
    }
  }
`;

const OfferText = styled.p`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.05rem, 2.5vw, 1.35rem);
  font-weight: 600;
  color: #fff;
  line-height: 1.35;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.45;
  }
`;

const OfferTag = styled.span`
  display: inline-block;
  background: ${H.torch};
  color: #fff;
  padding: 0.3rem 0.8rem;
  border-radius: 4px;
  font-family: system-ui, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 768px) {
    align-self: center;
  }
`;

/* ─── GROUP SAVER TIERS ─── */
const SaverGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(224,122,47,0.2);

  @media (max-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const SaverTier = styled.div`
  background: ${p => (p.$active ? H.torch : p.$dark ? H.plum : H.parchment)};
  padding: clamp(1rem, 2.5vw, 1.5rem) 1rem;
  text-align: center;
`;

const SaverTickets = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.85rem;
  font-weight: 700;
  color: ${p => (p.$light ? '#fff' : H.ink)};
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const SaverDiscount = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${p => (p.$light ? 'rgba(255,255,255,0.8)' : H.moss)};
  margin-bottom: 0.5rem;
`;

const SaverLabel = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.65rem;
  color: ${p => (p.$light ? 'rgba(255,255,255,0.55)' : 'rgba(20,16,31,0.5)')};
  letter-spacing: 0.06em;
`;

/* ─── SECTIONS ─── */
const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid rgba(20,16,31,0.07)` : 'none'};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${p => p.$color || H.torch};
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.85rem, 4.5vw, 3rem);
  font-weight: 700;
  line-height: 1.08;
  color: ${p => p.$light ? '#fff' : H.ink};

  em {
    font-style: italic;
    color: ${H.torch};
  }
`;

const SectionIntro = styled.p`
  max-width: 640px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.65;
  color: ${p => p.$light ? 'rgba(255,255,255,0.72)' : 'rgba(20,16,31,0.62)'};
  margin: 0 0 clamp(2rem, 4vw, 3rem);
`;

const Divider = styled.div`
  width: 52px;
  height: 2px;
  background: ${H.torch};
  margin-bottom: clamp(1.25rem, 2.5vw, 1.75rem);
`;

/* ─── TRAIL STEPS ─── */
const TrailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3px;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: clamp(2.5rem, 5vw, 3.5rem);

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const TrailStep = styled.div`
  background: ${p => p.$bg};
  padding: clamp(1.75rem, 3.5vw, 2.5rem) clamp(1.5rem, 3vw, 2rem);
  color: #fff;
`;

const TrailNum = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 3rem;
  font-weight: 700;
  color: rgba(255,255,255,0.2);
  line-height: 1;
  margin-bottom: 1rem;
`;

const TrailStepTitle = styled.h3`
  margin: 0 0 0.6rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.35rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.15;
`;

const TrailStepBody = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.65;
  color: rgba(255,255,255,0.76);
`;

/* ─── FEATURE CARDS ─── */
const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: clamp(1rem, 2vw, 1.5rem);
`;

const FeatureCard = styled.div`
  background: ${p => p.$dark ? H.ink : '#fff'};
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${p => p.$dark ? 'transparent' : 'rgba(20,16,31,0.08)'};
  box-shadow: 0 4px 24px rgba(20,16,31,0.07);
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(224,122,47,0.1);
  border-radius: 10px;
  margin-bottom: 1rem;
  flex-shrink: 0;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.5rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : H.ink};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.6;
  color: ${p => p.$light ? 'rgba(255,255,255,0.68)' : 'rgba(20,16,31,0.6)'};
`;

/* ─── SECTION BREAKER ─── */
const SectionBreaker = styled.section`
  position: relative;
  width: 100%;
  height: ${p => p.$contained ? 'clamp(480px, 58vh, 680px)' : 'clamp(560px, 58vh, 720px)'};
  overflow: hidden;
  ${p => p.$contained && `
    margin-top: clamp(2rem, 4vw, 3rem);
    border-radius: 14px;
    box-shadow: 0 8px 32px rgba(20,16,31,0.12);
  `}
`;

const BreakerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 40%;
  display: block;
`;

const BreakerOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(to right, rgba(20,16,31,0.88) 0%, rgba(20,16,31,0.42) 32%, transparent 56%),
    linear-gradient(to bottom, transparent 48%, rgba(20,16,31,0.52) 100%);

  @media (max-width: 768px) {
    background:
      linear-gradient(to bottom, transparent 35%, rgba(20,16,31,0.88) 100%);
  }
`;

const BreakerQuoteWrap = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  padding: clamp(1.5rem, 5vw, 4rem);
  max-width: min(520px, 44vw);

  @media (max-width: 768px) {
    align-items: flex-end;
    justify-content: center;
    max-width: none;
    padding: 2rem 1.5rem 2.5rem;
  }
`;

const BreakerQuote = styled.p`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.5rem, 3.2vw, 2.5rem);
  font-style: italic;
  font-weight: 300;
  color: #fff;
  line-height: 1.35;
  text-shadow: 0 2px 28px rgba(0,0,0,0.5);

  @media (max-width: 768px) {
    text-align: center;
    max-width: 22rem;
  }
`;

/* ─── THE STORY ─── */
const CursePanel = styled.div`
  background: ${H.ink};
  border-radius: 10px;
  border-left: 4px solid ${H.torch};
  padding: clamp(1.5rem, 3vw, 2.25rem) clamp(1.5rem, 4vw, 2.5rem);
  text-align: center;
  margin-bottom: clamp(2rem, 4vw, 3rem);
`;

const CursePanelLabel = styled.p`
  margin: 0 0 0.75rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(224,122,47,0.75);
`;

const CursePanelQuote = styled.p`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.2rem, 2.6vw, 1.65rem);
  font-style: italic;
  font-weight: 600;
  color: #fff;
  line-height: 1.55;
`;

/* ─── SAFARI SQUAD CARDS ─── */
const SquadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 3px;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: clamp(2rem, 4vw, 3rem);

  @media (max-width: 860px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const SquadCard = styled.div`
  background: ${H.bone};
  border-top: 3px solid ${p => p.$accent};
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
`;

const SquadRole = styled.p`
  margin: 0 0 0.3rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${p => p.$accent};
`;

const SquadName = styled.h3`
  margin: 0 0 0.5rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.45rem;
  font-weight: 700;
  color: ${H.ink};
`;

const SquadBody = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.82rem;
  line-height: 1.6;
  color: rgba(20,16,31,0.62);
`;

/* ─── PULLQUOTE ─── */
const PullQuote = styled.blockquote`
  margin: 0 0 clamp(2rem, 4vw, 3rem);
  padding: clamp(1.5rem, 3vw, 2.25rem) clamp(1.5rem, 4vw, 2.5rem);
  background: ${H.ink};
  border-left: 4px solid ${H.torch};
  border-radius: 0 12px 12px 0;

  p {
    margin: 0 0 0.65rem;
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: clamp(1.25rem, 2.8vw, 1.65rem);
    font-style: italic;
    font-weight: 300;
    color: #fff;
    line-height: 1.45;

    &:last-of-type { margin-bottom: 0; }
  }

  cite {
    display: block;
    margin-top: 0.85rem;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: ${H.moss};
    font-style: normal;
  }
`;

/* ─── TWO-COLUMN ─── */
const TwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: ${p => p.$align || 'start'};

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const AnimalStrip = styled.div`
  overflow: hidden;
  border-radius: 14px;

  img {
    width: 100%;
    height: clamp(340px, 48vw, 520px);
    object-fit: cover;
    object-position: center 22%;
    display: block;
  }
`;

/* ─── GALLERY ─── */
const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-auto-rows: 260px;
  gap: clamp(0.5rem, 1vw, 0.75rem);
  border-radius: 14px;
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: 180px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: 160px;
  }
`;

const GalleryItem = styled.div`
  overflow: hidden;
  ${p => p.$span2 ? 'grid-row: span 2;' : ''}
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }

  @media (max-width: 768px) {
    ${p => p.$span2 ? 'grid-row: span 1;' : ''}
  }
`;

/* ─── MID-CTA ─── */
const MidCta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.5rem;
  margin-top: clamp(2rem, 4vw, 2.75rem);
  padding: clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 4vw, 2rem);
  border-radius: 12px;
  background: ${p => (p.$dark ? H.ink : H.parchment)};
  border: 1px solid rgba(224,122,47,0.22);
`;

const MidCtaText = styled.p`
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(1.05rem, 2.2vw, 1.3rem);
  font-weight: 600;
  line-height: 1.35;
  color: ${p => (p.$light ? '#fff' : H.ink)};
  flex: 1;
  min-width: min(100%, 16rem);
`;

const MidCtaBtn = styled.button`
  flex-shrink: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.9rem 1.75rem;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${H.torch};
  color: #fff;
  box-shadow: 0 4px 18px rgba(224,122,47,0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 22px rgba(224,122,47,0.46);
  }
`;

/* ─── ESSENTIAL INFO ─── */
const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1px;
  background: rgba(20,16,31,0.08);
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
`;

const InfoLabel = styled.div`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(20,16,31,0.4);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: ${H.ink};
  line-height: 1.25;
`;

/* ─── CTA ─── */
const CtaSection = styled.section`
  background: ${H.ink};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(60deg, transparent, transparent 38px, rgba(224,122,47,0.05) 38px, rgba(224,122,47,0.05) 40px),
      repeating-linear-gradient(-60deg, transparent, transparent 38px, rgba(92,58,78,0.08) 38px, rgba(92,58,78,0.08) 40px);
    pointer-events: none;
  }
`;

const CtaInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;

  em {
    font-style: italic;
    color: ${H.candle};
  }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.65);
  line-height: 1.6;
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  align-items: center;
`;

const CtaBtn = styled.button`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1.15rem 2.75rem;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${H.torch};
  color: #fff;
  box-shadow: 0 8px 32px rgba(224,122,47,0.42);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(224,122,47,0.54);
  }
`;

const CtaMeta = styled.p`
  margin: 1.25rem 0 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.35);
  letter-spacing: 0.06em;
`;

/* ─────────────────────────────────────
   COMPONENT
───────────────────────────────────── */
const WickedNight = () => {
  const navigate = useNavigate();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeImg, setActiveImg] = useState(null);

  const bookTickets = () => {
    navigate('/halloween-belvoir-castle/tickets');
  };

  const openLightbox = (src) => {
    setActiveImg(src);
    setGalleryOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>Halloween at Belvoir Castle — Best Family Halloween Day Out Leicestershire | Enchanted Halloween Trail 2026</title>
        <meta
          name="description"
          content="Belvoir's Enchanted Halloween Trail — the best family Halloween day out in Leicestershire this October. Adventure playground trail, Animal Park included, free Curse Breaker treat. From £6 child / £10 adult. Group Saver when you book together. October weekends & school half-term 2026. Silly, never scary."
        />
        <meta
          name="keywords"
          content="Halloween Belvoir Castle, best family Halloween day out Leicestershire, Halloween trail East Midlands, Halloween half term 2026, family Halloween adventure Grantham, things to do Halloween Nottinghamshire, Halloween Lincolnshire kids, Belvoir Enchanted Halloween Trail, Animal Park Halloween, Curse Breaker trail, best Halloween days out near me, Melton Mowbray Halloween, top UK family Halloween attractions 2026, Group Saver Belvoir, Halloween tickets Belvoir Castle"
        />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <meta name="author" content="Belvoir Castle" />
        <link rel="canonical" href={getCanonicalUrl('halloween-belvoir-castle')} />
        <link
          rel="alternate"
          type="text/html"
          href="https://www.belvoircastle.com/halloween-belvoir-castle.html"
          title="Belvoir's Enchanted Halloween Trail — static readable version"
        />
        <link rel="help" href="https://www.belvoircastle.com/llms.txt" title="Belvoir Castle AI-readable content index" />

        {/* ── Open Graph ── */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:title" content="Halloween at Belvoir Castle — Best Family Halloween Day Out | Enchanted Halloween Trail 2026" />
        <meta
          property="og:description"
          content="Collect spell ingredients. Find the password animal. Brew the counter-curse. Family Halloween adventure — Animal Park included, free Curse Breaker treat. From £6 child / £10 adult. Group Saver online. October 2026. Silly, never scary."
        />
        <meta property="og:url" content={getCanonicalUrl('halloween-belvoir-castle')} />
        <meta property="og:image" content={HERO_IMG} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Pesky witches on the adventure playground — Belvoir's Enchanted Halloween Trail 2026" />
        <meta property="og:locale" content="en_GB" />

        {/* ── Twitter / X ── */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@BelvoirCastle" />
        <meta name="twitter:title" content="Halloween at Belvoir Castle — Best Family Halloween Day Out 2026" />
        <meta
          name="twitter:description"
          content="Enchanted Halloween adventure trail — Animal Park included, free Curse Breaker treat. From £6 child / £10 adult. Group Saver when you book 2+. October weekends & half-term 2026."
        />
        <meta name="twitter:image" content={HERO_IMG} />
        <meta name="twitter:image:alt" content="Belvoir's Enchanted Halloween Trail 2026 — family Halloween adventure at Belvoir Castle" />

        {/* ── Event JSON-LD ── */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: "Belvoir's Enchanted Halloween Trail",
            alternateName: [
              'Halloween at Belvoir Castle',
              'Belvoir Castle Halloween 2026',
              'Belvoir Curse Breaker Trail',
            ],
            description:
              "Belvoir's Enchanted Halloween Trail is Leicestershire's premier family Halloween event — an adventure playground trail where Curse Breakers collect spell ingredients, find the password animal in the Animal Park, and brew the counter-curse at the Retail Village cauldron. Animal Park included. Free Curse Breaker treat for every child who completes the trail. Gardens & Playground tickets from £10 adult / £6 child. Group Saver when you book 2+ online. Silly, never scary. October weekends and school half-term 2026.",
            image: [
              HERO_IMG,
              IMG_GIRL_CURSE,
              IMG_BOY_GOAT,
              IMG_PLAY_TOWER,
            ],
            url: getCanonicalUrl('halloween-belvoir-castle'),
            startDate: '2026-10-01',
            endDate: '2026-10-31',
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            location: {
              '@type': 'Place',
              name: 'Belvoir Castle',
              description:
                "Historic castle estate with formal gardens, adventure playground, Animal Park and Retail Village — Leicestershire's complete family day out.",
              address: {
                '@type': 'PostalAddress',
                streetAddress: 'Belvoir Castle',
                addressLocality: 'Grantham',
                postalCode: 'NG32 1PE',
                addressRegion: 'Leicestershire',
                addressCountry: 'GB',
              },
              geo: { '@type': 'GeoCoordinates', latitude: 52.8893, longitude: -0.7814 },
            },
            organizer: {
              '@type': 'Organization',
              name: 'Belvoir Castle',
              url: 'https://www.belvoircastle.com',
            },
            offers: {
              '@type': 'AggregateOffer',
              url: 'https://www.belvoircastle.com/halloween-belvoir-castle/tickets',
              priceCurrency: 'GBP',
              lowPrice: '6.00',
              highPrice: '10.00',
              offerCount: 2,
              availability: 'https://schema.org/InStock',
              validFrom: '2026-08-01',
              description:
                'Gardens & Playground entry — Halloween adventure trail, Animal Park and Curse Breaker treat included. Adult £10, Child £6. Group Saver for 2+ tickets booked online. HHA and RHS members pay £4.50 for Animal Park entry (addable online). Optional Halloween crafts £4.50.',
              offers: [
                {
                  '@type': 'Offer',
                  name: 'Adult — Gardens & Playground Entry (Halloween Trail)',
                  category: 'Adult',
                  price: '10.00',
                  priceCurrency: 'GBP',
                  availability: 'https://schema.org/InStock',
                  url: 'https://www.belvoircastle.com/halloween-belvoir-castle/tickets',
                  validFrom: '2026-08-01',
                  priceValidUntil: '2026-10-31',
                  description:
                    'Adult Gardens & Playground ticket for Belvoir\'s Enchanted Halloween Trail. Includes adventure playground trail, Animal Park and Curse Breaker treat for completing children. £10.',
                },
                {
                  '@type': 'Offer',
                  name: 'Child — Gardens & Playground Entry (Halloween Trail)',
                  category: 'Child',
                  price: '6.00',
                  priceCurrency: 'GBP',
                  availability: 'https://schema.org/InStock',
                  url: 'https://www.belvoircastle.com/halloween-belvoir-castle/tickets',
                  validFrom: '2026-08-01',
                  priceValidUntil: '2026-10-31',
                  description:
                    'Child Gardens & Playground ticket (over 3–17, from the 3rd birthday) for Belvoir\'s Enchanted Halloween Trail. Includes adventure playground trail, Animal Park and free Curse Breaker treat on completion. £6. Under 3 is free until the 3rd birthday.',
                },
              ],
            },
            audience: { '@type': 'Audience', audienceType: 'Families with children' },
            keywords:
              'best family Halloween day out, Halloween half term 2026, Halloween adventure trail, animal park, Leicestershire, Belvoir Castle, East Midlands Halloween, Curse Breaker',
          })}
        </script>

        {/* ── FAQPage JSON-LD ── */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How much are Halloween tickets at Belvoir Castle?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Gardens & Playground entry for Belvoir\'s Enchanted Halloween Trail is £10 for adults and £6 for children. Tickets include the Halloween adventure trail, Animal Park, and a free Curse Breaker treat for every child who completes the trail. Group Saver applies automatically when you book 2 or more tickets online. Optional Halloween crafts are £4.50.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is Belvoir\'s Enchanted Halloween Trail a good family Halloween day out?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes — it is widely regarded as one of the best family Halloween days out in Leicestershire and the East Midlands for October 2026. It combines an adventure playground Halloween trail, real animals in the Animal Park (included), a free Curse Breaker treat, formal gardens, and Retail Village dining and crafts. Properly Halloween — silly, never scary. Adult tickets £10, child tickets £6.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is the Animal Park included in the Halloween ticket price?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. The Animal Park is included with Gardens & Playground entry (£10 adult / £6 child) — there is no extra charge for standard ticket holders. HHA Members and RHS Members pay £4.50 for Animal Park entry, addable when booking online.',
                },
              },
              {
                '@type': 'Question',
                name: 'Do HHA or RHS members need to pay for the Animal Park?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. HHA Members and RHS Members are required to pay £4.50 to enter the Animal Park. This can be added when booking online.',
                },
              },
              {
                '@type': 'Question',
                name: 'What is the free Curse Breaker treat?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Every child who completes Belvoir\'s Enchanted Halloween Trail earns a free treat and the official title of Belvoir Castle Curse Breaker at the cauldron finale in the Retail Village.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is there a discount for groups or families?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. Belvoir Group Saver applies to all online Gardens bookings of 2 or more tickets. The more tickets you book together, the bigger the saving — applied automatically at checkout. Adult tickets are £10 and child tickets are £6 before Group Saver. Group Saver is not applied to standalone crafts bookings.',
                },
              },
              {
                '@type': 'Question',
                name: 'When does Belvoir\'s Enchanted Halloween Trail run?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'October weekends and the October school half-term holidays in 2026. Belvoir Castle remains closed on Tuesdays and Wednesdays, including during October half term. Book early — popular half-term dates can sell out.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is Halloween at Belvoir scary for young children?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'No. Properly Halloween — silly, never scary. Mission-led and completely family-friendly with no jump scares or horror themes.',
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <Page>

        {/* ── HERO ── */}
        <HeroWrap>
          <HeroImageThenVideo image={HERO_IMG} videoSrc={halloweenVideo} delay={3000} />
          <HeroOverlay />
          <HeroPattern />
          <HeroContent>
            <HeroKicker>October School Holidays 2026 · Silly, Not Scary</HeroKicker>
            <HeroTitle>
              Belvoir&rsquo;s <em>Enchanted Halloween Trail</em>
            </HeroTitle>
            <HeroStrapline>
              A pesky witch has cursed Belvoir. Can you break the spell?
            </HeroStrapline>
            <HeroBadgeRow>
              <Badge $fill>Group Saver — book together &amp; save</Badge>
              <Badge>Animal Park Included</Badge>
              <Badge>Treat &amp; Title for Every Curse Breaker</Badge>
            </HeroBadgeRow>
            <HeroButtons>
              <BtnPrimary onClick={bookTickets}>
                Book Tickets
              </BtnPrimary>
              <BtnGhost
                type="button"
                onClick={() => document.getElementById('trail-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Discover the Trail
              </BtnGhost>
            </HeroButtons>
          </HeroContent>
        </HeroWrap>

        {/* ── GROUP SAVER BANNER ── */}
        <OfferBanner>
          <OfferTag>Group Saver</OfferTag>
          <OfferText>
            Bring friends and family this October — book <strong>2 or more tickets together online</strong> and save automatically. The more you book, the more you save. <strong>Book early</strong> to secure half-term dates.
          </OfferText>
          <BtnPrimary
            style={{ padding: '0.65rem 1.5rem', fontSize: '0.75rem' }}
            onClick={bookTickets}
          >
            Book Now
          </BtnPrimary>
        </OfferBanner>

        {/* ── THE TRAIL ── */}
        <Section $bg={H.bone} $border id="trail-section">
          <Inner>
            <Kicker $color={H.torch}>The Trail</Kicker>
            <Divider />
            <SectionTitle>
              Three Steps.<br /><em>One Counter-Curse.</em>
            </SectionTitle>
            <SectionIntro>
              A Halloween adventure trail that takes your Curse Breakers from the enchanted adventure playground, through the Animal Park, and to the cauldron in the Retail Village — before midnight.
            </SectionIntro>

            <TrailGrid>
              <TrailStep $bg={H.moss}>
                <TrailNum>01</TrailNum>
                <TrailStepTitle>Collect the Ingredients</TrailStepTitle>
                <TrailStepBody>
                  Spell ingredients are hidden across the enchanted adventure playground. Find them all — your counter-curse depends on it.
                </TrailStepBody>
              </TrailStep>
              <TrailStep $bg={H.torch}>
                <TrailNum>02</TrailNum>
                <TrailStepTitle>Find the Password Animal</TrailStepTitle>
                <TrailStepBody>
                  Visit the Safari Squad in the Animal Park. One of Stuart&rsquo;s cursed friends holds the secret password — find them.
                </TrailStepBody>
              </TrailStep>
              <TrailStep $bg={H.plum}>
                <TrailNum>03</TrailNum>
                <TrailStepTitle>Brew the Counter-Curse</TrailStepTitle>
                <TrailStepBody>
                  The curse is growing stronger! Find the cauldron in the Retail Village, say the magic password and Free Stuart&rsquo;s friends from the spell and earn a reward fit for a brave Curse Breaker.
                </TrailStepBody>
              </TrailStep>
            </TrailGrid>

            <PullQuote>
              <p>&ldquo;I escaped. I was too small, too quick. But Gary, Katie, Dave — they weren&rsquo;t so lucky. I can&rsquo;t break the spell alone.&rdquo;</p>
              <p>&ldquo;I&rsquo;ll know a proper Curse Breaker when I see one.&rdquo;</p>
              <cite>— Stuart the Calf, Belvoir Animal Park</cite>
            </PullQuote>

            <MidCta>
              <MidCtaText>Planning a Halloween day out with friends or family? Group Saver applies when you book 2+ tickets online — book early to secure your spot.</MidCtaText>
              <MidCtaBtn type="button" onClick={bookTickets}>Book Tickets</MidCtaBtn>
            </MidCta>

            <SectionBreaker $contained>
              <BreakerImg src={IMG_GIRL_CURSE} alt="A young Curse Breaker on the Halloween trail at Belvoir" />
              <BreakerOverlay />
              <BreakerQuoteWrap>
                <BreakerQuote>
                  &ldquo;The animals are waiting. Help Stuart free his friends.&rdquo;
                </BreakerQuote>
              </BreakerQuoteWrap>
            </SectionBreaker>
          </Inner>
        </Section>

        {/* ── WHAT'S INCLUDED ── */}
        <Section $bg="#fff" $border>
          <Inner>
            <Kicker>What&rsquo;s Waiting</Kicker>
            <Divider />
            <SectionTitle>Everything a <em>Curse Breaker Needs</em></SectionTitle>
            <FeatureGrid>
              <FeatureCard>
                <FeatureIcon>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={H.torch} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2l2 7h7l-5.5 4 2 7L12 16l-5.5 4 2-7L3 9h7z"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle>Enchanted Playground Trail</FeatureTitle>
                <FeatureBody>Spell ingredients hidden across the adventure playground. The first step to brewing your counter-curse — and the start of a properly Halloween adventure.</FeatureBody>
              </FeatureCard>

              <FeatureCard>
                <FeatureIcon>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill={H.torch}>
                    <ellipse cx="6.5" cy="7.5" rx="2" ry="2.5"/>
                    <ellipse cx="11" cy="5.5" rx="2" ry="2.5"/>
                    <ellipse cx="15.5" cy="7" rx="2" ry="2.5"/>
                    <path d="M5 15c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5c0 2.5-1.8 4.5-7 4.5S5 17.5 5 15z"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle>The Enchanted Animal Park</FeatureTitle>
                <FeatureBody>Meet the Safari Squad — Gary, Katie, Dave and Stuart. Three of them are cursed. One holds the secret password. Included with every ticket.</FeatureBody>
              </FeatureCard>

              <FeatureCard>
                <FeatureIcon>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={H.torch} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4m0 0h18"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle>The Cauldron Finale</FeatureTitle>
                <FeatureBody>Say the password. Brew the counter-curse at the cauldron in the Retail Village. The spell breaks — and every Curse Breaker earns a treat and the official title.</FeatureBody>
              </FeatureCard>

              <FeatureCard $dark>
                <FeatureIcon style={{ background: 'rgba(224,122,47,0.18)' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={H.torch} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle $light>Stuart&rsquo;s Animal Tale</FeatureTitle>
                <FeatureBody $light>Read Stuart&rsquo;s tale before you visit so your Curse Breakers know exactly who they&rsquo;re coming to help.</FeatureBody>
              </FeatureCard>

              <FeatureCard>
                <FeatureIcon>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={H.torch} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="6"/>
                    <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle>Curse Breaker Title &amp; Treat</FeatureTitle>
                <FeatureBody>Every child who completes the trail earns a treat and the official title: Belvoir Castle Curse Breaker. Because every adventure deserves a proper ending.</FeatureBody>
              </FeatureCard>

              <FeatureCard>
                <FeatureIcon>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={H.torch} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="10" width="18" height="11" rx="1"/>
                    <path d="M3 10V7h3v3"/>
                    <path d="M10.5 10V7h3v3"/>
                    <path d="M18 10V7h3v3"/>
                    <path d="M9 21v-4a3 3 0 0 1 6 0v4"/>
                  </svg>
                </FeatureIcon>
                <FeatureTitle>Silly, Never Scary</FeatureTitle>
                <FeatureBody>Properly Halloween — completely family-friendly. A pesky witch, a cursed playground, enchanted animals, and a cauldron. Mission-led, not frightening.</FeatureBody>
              </FeatureCard>
            </FeatureGrid>

            <MidCta>
              <MidCtaText>Trail, Animal Park, treat &amp; the title — all part of Belvoir&rsquo;s Enchanted Halloween Trail.</MidCtaText>
              <MidCtaBtn type="button" onClick={bookTickets}>Book Your Visit</MidCtaBtn>
            </MidCta>
          </Inner>
        </Section>

        {/* ── THE STORY ── */}
        <Section $bg={H.bone} $border>
          <Inner>
            <TwoCols $cols="1.1fr 0.9fr" $align="center">
              <div>
                <Kicker $color={H.plum}>The Story</Kicker>
                <Divider />
                <SectionTitle>
                  She flew over the castle<br /><em>and cast a spell.</em>
                </SectionTitle>
                <SectionIntro>
                  A pesky witch appeared at Belvoir and cursed the whole estate. The adventure playground is enchanted. The Animal Park animals have been transformed. Even the cauldron in the Retail Village has started bubbling.
                </SectionIntro>
                <SectionIntro style={{ marginBottom: 0 }}>
                  Only a properly brewed counter-curse can break the spell — and that means Curse Breakers. That&rsquo;s you.
                </SectionIntro>

                <CursePanel style={{ marginTop: 'clamp(1.5rem, 3vw, 2rem)', textAlign: 'left' }}>
                  <CursePanelLabel>The Witch&rsquo;s Curse</CursePanelLabel>
                  <CursePanelQuote>
                    &ldquo;I curse this estate and every creature in it. Every animal shall be transformed until a counter-curse is brewed.&rdquo;
                  </CursePanelQuote>
                </CursePanel>
              </div>
              <AnimalStrip>
                <img src={IMG_WITCH_ZIP} alt="A pesky witch on the zip line at Belvoir Castle" />
              </AnimalStrip>
            </TwoCols>

            <GalleryGrid style={{ marginTop: 'clamp(1.5rem, 3vw, 2.25rem)', gridTemplateRows: '220px 220px' }}>
              <GalleryItem $span2 onClick={() => openLightbox(IMG_WITCHES_PATH)}>
                <img src={IMG_WITCHES_PATH} alt="The pesky witches waving on the trail at Belvoir Castle" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(IMG_WITCH_PURPLE)}>
                <img src={IMG_WITCH_PURPLE} alt="A pesky witch on the adventure playground at Belvoir Castle" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(IMG_WITCH_FEATHERS)}>
                <img src={IMG_WITCH_FEATHERS} alt="A pesky witch casting a spell at Belvoir Castle" />
              </GalleryItem>
            </GalleryGrid>
          </Inner>
        </Section>

        {/* ── MEET THE SAFARI SQUAD ── */}
        <Section $bg="#fff" $border>
          <Inner>
            <Kicker $color={H.moss}>Meet the Safari Squad</Kicker>
            <Divider />
            <SectionTitle>
              Real animals. Real names.<br /><em>One of them escaped.</em>
            </SectionTitle>
            <SectionIntro>
              The Safari Squad live in the Belvoir Animal Park. This October, all of them are enchanted — apart from Stuart.
            </SectionIntro>

            <SquadGrid>
              <SquadCard $accent={H.moss}>
                <SquadRole $accent={H.moss}>Calf · Escaped</SquadRole>
                <SquadName>Stuart</SquadName>
                <SquadBody>Too small, too quick. The witch didn&rsquo;t spot him in the dark. Ears flat. Asking for Curse Breakers.</SquadBody>
              </SquadCard>
              <SquadCard $accent={H.torch}>
                <SquadRole $accent={H.torch}>Goat · Enchanted</SquadRole>
                <SquadName>Gary</SquadName>
                <SquadBody>Quick, curious, always one step ahead — until the witch got there first. Stuart is particularly worried about him.</SquadBody>
              </SquadCard>
              <SquadCard $accent={H.plum}>
                <SquadRole $accent={H.plum}>Goat · Enchanted</SquadRole>
                <SquadName>Katie</SquadName>
                <SquadBody>Full speed, no hesitation. Waiting in the Animal Park until a Curse Breaker finds the password.</SquadBody>
              </SquadCard>
              <SquadCard $accent={H.candle}>
                <SquadRole $accent={H.candle}>Donkey · Enchanted</SquadRole>
                <SquadName>Dave</SquadName>
                <SquadBody>Quiet. Watchful. The kind of gentle that stays with you — and he needs freeing too.</SquadBody>
              </SquadCard>
            </SquadGrid>

            <div
              style={{
                marginTop: 'clamp(1.25rem, 3vw, 1.75rem)',
                marginBottom: 'clamp(1.25rem, 3vw, 1.75rem)',
                borderRadius: 14,
                overflow: 'hidden',
                cursor: 'pointer',
              }}
              onClick={() => openLightbox(IMG_DONKEYS)}
            >
              <img
                src={IMG_DONKEYS}
                alt="Meeting the donkeys in the Belvoir Animal Park"
                style={{
                  width: '100%',
                  height: 'clamp(220px, 32vw, 380px)',
                  objectFit: 'cover',
                  objectPosition: 'center 40%',
                  display: 'block',
                }}
              />
            </div>

            <MidCta style={{ marginTop: 'clamp(1.25rem, 3vw, 1.75rem)' }}>
              <MidCtaText>
                Meet Stuart properly — read his Animal Tale before you visit.
              </MidCtaText>
              <MidCtaBtn
                as="a"
                href="https://www.belvoircastle.com/animal-tale-stuart-the-calf.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                Read Stuart&rsquo;s Story
              </MidCtaBtn>
            </MidCta>
          </Inner>
        </Section>

        {/* ── SECTION BREAKER 2 ── */}
        <SectionBreaker aria-hidden>
          <BreakerImg src={IMG_BOY_GOAT} alt="" style={{ objectPosition: '50% 40%' }} />
          <BreakerOverlay />
          <BreakerQuoteWrap>
            <BreakerQuote>
              &ldquo;The Safari Squad — before the curse. Come and free them.&rdquo;
            </BreakerQuote>
          </BreakerQuoteWrap>
        </SectionBreaker>

        {/* ── CRAFTS (AFTER THE STORY) ── */}
        <Section $bg={H.bone} $border>
          <Inner>
            <TwoCols $cols="1fr 1fr" $align="center">
              <div>
                <Kicker $color={H.plum}>Also This October</Kicker>
                <Divider />
                <SectionTitle>
                  Retail Village crafts.<br /><em>Take the magic home.</em>
                </SectionTitle>
                <SectionIntro>
                  The trail ends at the cauldron in the Retail Village — and that&rsquo;s where the craft tables begin. Hands-on Halloween crafts that continue the story, then go home with you.
                </SectionIntro>
                <SectionIntro style={{ marginBottom: 0 }}>
                  Crafts are £4.50 at the Retail Village — add them to your Gardens visit, or book as a standalone session. Ask at the cauldron on the day if spaces remain.
                </SectionIntro>
                <MidCta style={{ marginTop: 'clamp(1.5rem, 3vw, 2rem)' }}>
                  <MidCtaText>Halloween crafts — only £4.50</MidCtaText>
                  <MidCtaBtn type="button" onClick={() => navigate('/halloween-crafts')}>Discover Crafts</MidCtaBtn>
                </MidCta>
              </div>
              <GalleryGrid style={{ gridTemplateRows: '220px 220px' }}>
                <GalleryItem $span2 onClick={() => openLightbox(IMG_CRAFTS_HERO)} style={{ cursor: 'pointer' }}>
                  <img src={IMG_CRAFTS_HERO} alt="Children in Halloween costumes crafting and carving pumpkins at Belvoir" />
                </GalleryItem>
                <GalleryItem onClick={() => openLightbox(IMG_CRAFTS)} style={{ cursor: 'pointer' }}>
                  <img src={IMG_CRAFTS} alt="Families decorating pumpkins at a Halloween craft session at Belvoir" />
                </GalleryItem>
                <GalleryItem onClick={() => openLightbox(IMG_CRAFTS_2)} style={{ cursor: 'pointer' }}>
                  <img src={IMG_CRAFTS_2} alt="A child painting a pumpkin at a Halloween craft session at Belvoir" />
                </GalleryItem>
              </GalleryGrid>
            </TwoCols>
          </Inner>
        </Section>

        {/* ── GALLERY ── */}
        <Section $bg="#fff" $border>
          <Inner>
            <Kicker>The Experience</Kicker>
            <Divider />
            <SectionTitle>A Day They&rsquo;ll <em>Talk About</em></SectionTitle>
            <GalleryGrid>
              <GalleryItem $span2 onClick={() => openLightbox(IMG_PLAY_TOWER)}>
                <img src={IMG_PLAY_TOWER} alt="The adventure playground tower at Belvoir Castle" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(IMG_PLAY_CLIMB)}>
                <img src={IMG_PLAY_CLIMB} alt="A Curse Breaker climbing the adventure playground at Belvoir" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(IMG_PLAY_ZIP)}>
                <img src={IMG_PLAY_ZIP} alt="A Curse Breaker on the zip line at Belvoir" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(IMG_COLLAGE)}>
                <img src={IMG_COLLAGE} alt="Belvoir's Enchanted Halloween Trail collage — playground, animals and Curse Breakers" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(IMG_HALLOWEEN_BOY)}>
                <img src={IMG_HALLOWEEN_BOY} alt="A young Curse Breaker on the enchanted adventure playground at Belvoir" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(IMG_DONKEYS)}>
                <img src={IMG_DONKEYS} alt="Meeting the donkeys in the Belvoir Animal Park" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(IMG_BOY_GOAT)}>
                <img src={IMG_BOY_GOAT} alt="A Curse Breaker meeting a goat at Belvoir Animal Park" />
              </GalleryItem>
              <GalleryItem onClick={() => openLightbox(IMG_GIRL_CURSE)}>
                <img src={IMG_GIRL_CURSE} alt="A young Curse Breaker on the Halloween trail at Belvoir" />
              </GalleryItem>
            </GalleryGrid>
            <MidCta>
              <MidCtaText>Give your Curse Breakers a Halloween day they&rsquo;ll remember.</MidCtaText>
              <MidCtaBtn type="button" onClick={bookTickets}>Book Now</MidCtaBtn>
            </MidCta>
          </Inner>
        </Section>

        {/* ── GROUP SAVER ── */}
        <Section $bg={H.midnight}>
          <Inner>
            <Kicker $color={H.torch}>Group Saver</Kicker>
            <Divider />
            <SectionTitle $light>
              Bring More Curse Breakers.<br /><em>Save More.</em>
            </SectionTitle>
            <SectionIntro $light>
              The more tickets you book together, the bigger the saving. Whether you&rsquo;re coming with grandparents, cousins, or a group of friends — Group Saver makes it easy to bring everyone along this October. Book early to secure your half-term dates.
            </SectionIntro>

            <SaverGrid>
              {[
                { tickets: '2', tagline: 'Saver applies', label: 'tickets' },
                { tickets: '3', tagline: 'Save more', label: 'tickets' },
                { tickets: '4', tagline: 'Save more', label: 'tickets', active: true },
                { tickets: '5', tagline: 'Save more', label: 'tickets' },
                { tickets: '6+', tagline: 'Best saving', label: 'tickets', dark: true },
              ].map((tier) => (
                <SaverTier key={tier.tickets} $active={tier.active} $dark={tier.dark}>
                  <SaverTickets $light={tier.active || tier.dark}>{tier.tickets}</SaverTickets>
                  <SaverDiscount $light={tier.active || tier.dark}>{tier.tagline}</SaverDiscount>
                  <SaverLabel $light={tier.active || tier.dark}>{tier.label}</SaverLabel>
                </SaverTier>
              ))}
            </SaverGrid>

            <p
              style={{
                marginTop: '1.25rem',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '0.78rem',
                color: 'rgba(255,255,255,0.4)',
                letterSpacing: '0.04em',
              }}
            >
              Group Saver applies to all online Gardens bookings of 2 or more tickets. Adult and child tickets combined — savings applied automatically at checkout. Not applied to standalone crafts bookings.
            </p>

            <MidCta $dark style={{ marginTop: '2rem' }}>
              <MidCtaText $light>Bring the whole family — Group Saver when you book together online. Book early to avoid missing out.</MidCtaText>
              <MidCtaBtn type="button" onClick={bookTickets}>Book as a Group</MidCtaBtn>
            </MidCta>
          </Inner>
        </Section>

        {/* ── ESSENTIAL INFO ── */}
        <Section $bg={H.parchment} $border>
          <Inner>
            <Kicker>Plan Your Visit</Kicker>
            <Divider />
            <SectionTitle>Essential <em>Information</em></SectionTitle>
            <InfoGrid style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
              <InfoItem>
                <InfoLabel>When</InfoLabel>
                <InfoValue>October School<br />Holidays 2026</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Adult Ticket</InfoLabel>
                <InfoValue>£10<br />Gardens entry</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Child Ticket</InfoLabel>
                <InfoValue>£6 · Over 3–17<br />Gardens entry</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Animal Park</InfoLabel>
                <InfoValue>Included with<br />every ticket</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Reward</InfoLabel>
                <InfoValue>Treat &amp; official<br />Curse Breaker title</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Location</InfoLabel>
                <InfoValue>Belvoir Castle<br />NG32 1PE</InfoValue>
              </InfoItem>
            </InfoGrid>
            <p
              style={{
                margin: '0 0 clamp(1.5rem, 3vw, 2rem)',
                maxWidth: '40rem',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                fontSize: '0.88rem',
                lineHeight: 1.55,
                color: 'rgba(20,16,31,0.62)',
              }}
            >
              Gardens &amp; Playground tickets are <strong style={{ color: H.ink }}>£10 adult</strong> and{' '}
              <strong style={{ color: H.ink }}>£6 child</strong> — Halloween trail, Animal Park and Curse Breaker treat included.
              Group Saver applies automatically when you book 2 or more tickets online.
            </p>
          </Inner>
        </Section>

        {/* ── CTA ── */}
        <CtaSection>
          <CtaInner>
            <img
              src={PEACOCK}
              alt="Belvoir Castle"
              style={{ height: 52, width: 'auto', margin: '0 auto 1.5rem', display: 'block', opacity: 0.9 }}
            />
            <CtaTitle>
              Can you break the witch&rsquo;s curse<br /><em>before midnight?</em>
            </CtaTitle>
            <CtaBody>
              Book Belvoir&rsquo;s Enchanted Halloween Trail online with Group Saver — the more you book together, the more you save. Meet the Safari Squad. Free Stuart&rsquo;s friends. Earn the title of Belvoir Castle Curse Breaker.
            </CtaBody>
            <CtaBtnRow>
              <CtaBtn type="button" onClick={bookTickets}>
                Book Your Tickets
              </CtaBtn>
            </CtaBtnRow>
            <CtaMeta>
              October school holidays 2026 &nbsp;·&nbsp; Group Saver for online group bookings &nbsp;·&nbsp; Silly, never scary
            </CtaMeta>
          </CtaInner>
        </CtaSection>

        <Footer />
      </Page>

      {/* ── LIGHTBOX ── */}
      {galleryOpen && (
        <div
          onClick={() => setGalleryOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1.5rem', cursor: 'zoom-out',
          }}
        >
          <img
            src={activeImg}
            alt=""
            style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8 }}
            onClick={e => e.stopPropagation()}
          />
          <button
            onClick={() => setGalleryOpen(false)}
            style={{
              position: 'absolute', top: '1.25rem', right: '1.25rem',
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 999, width: 40, height: 40, color: '#fff', fontSize: '1.1rem',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>
      )}
    </>
  );
};

export default WickedNight;
